import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message
} from 'antd';
import { DeleteOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  addApiBatchItem,
  createApiBatch,
  createApiCase,
  deleteApiBatch,
  deleteApiBatchItem,
  deleteApiCase,
  executeApiBatch,
  executeApiRequest,
  getApiCatalog,
  listApiBatches,
  listApiCases,
  updateApiBatch,
  updateApiBatchItem,
  updateApiCase
} from '../../api/apiCenter';

const { Text, Paragraph } = Typography;

const METHOD_COLORS = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'gold',
  DELETE: 'red'
};

function rowsFromObject(obj) {
  return Object.entries(obj || {}).map(([key, value]) => ({ key, value: Array.isArray(value) ? value.join(',') : String(value ?? '') }));
}

function objectFromRows(rows) {
  return (rows || []).reduce((acc, row) => {
    const key = String(row?.key || '').trim();
    if (!key) return acc;
    const value = row?.value;
    if (value == null || value === '') return acc;
    acc[key] = value;
    return acc;
  }, {});
}

function safeJsonStringify(input) {
  try {
    return JSON.stringify(input ?? {}, null, 2);
  } catch (error) {
    return '{}';
  }
}

function extractPathParamKeys(path) {
  return Array.from(String(path || '').matchAll(/:([A-Za-z0-9_]+)/g)).map((match) => match[1]);
}

function createEmptyRow() {
  return { key: '', value: '' };
}

function createInitialRequest(endpoint) {
  const pathParamKeys = extractPathParamKeys(endpoint?.path);
  const pathParams = pathParamKeys.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
  const multipartTextRows = (endpoint?.multipartFields || [])
    .filter((field) => field.type !== 'file')
    .map((field) => ({ key: field.key, value: '' }));

  return {
    endpointId: endpoint?.id || '',
    method: endpoint?.method || 'GET',
    path: endpoint?.path || '',
    bodyType: endpoint?.bodyType || 'none',
    includeAuthToken: true,
    timeout: endpoint?.bodyType === 'multipart' ? 120000 : 60000,
    pathParams,
    queryRows: [],
    headerRows: [],
    jsonBodyText: safeJsonStringify(endpoint?.sampleBody || {}),
    rawBody: '',
    contentType: 'text/plain; charset=utf-8',
    multipartTextRows,
    multipartFiles: {}
  };
}

function KeyValueEditor({ title, rows, onChange, valuePlaceholder = '值' }) {
  const updateRow = (index, field, value) => {
    const next = [...rows];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const removeRow = (index) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    onChange(next);
  };

  const addRow = () => onChange([...(rows || []), createEmptyRow()]);

  return (
    <Card size="small" title={title} extra={<Button type="link" onClick={addRow}>新增</Button>}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {(rows || []).length ? rows.map((row, index) => (
          <Space key={`${title}-${index}`} align="start" style={{ width: '100%' }}>
            <Input
              placeholder="键"
              value={row.key}
              onChange={(event) => updateRow(index, 'key', event.target.value)}
              style={{ width: 180 }}
            />
            <Input
              placeholder={valuePlaceholder}
              value={row.value}
              onChange={(event) => updateRow(index, 'value', event.target.value)}
            />
            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removeRow(index)} />
          </Space>
        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无参数" />}
      </Space>
    </Card>
  );
}

export default function ApiCenterPage() {
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [casesLoading, setCasesLoading] = useState(false);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [caseSaving, setCaseSaving] = useState(false);
  const [batchExecuting, setBatchExecuting] = useState(false);
  const [groups, setGroups] = useState([]);
  const [cases, setCases] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedGroupKey, setSelectedGroupKey] = useState('');
  const [selectedEndpointId, setSelectedEndpointId] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [requestState, setRequestState] = useState(createInitialRequest(null));
  const [responseState, setResponseState] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [caseName, setCaseName] = useState('');
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchModalMode, setBatchModalMode] = useState('create');
  const [batchForm] = Form.useForm();

  const groupMap = useMemo(() => new Map(groups.map((group) => [group.key, group])), [groups]);
  const selectedGroup = groupMap.get(selectedGroupKey) || null;
  const selectedEndpoint = useMemo(() => {
    const endpoints = selectedGroup?.endpoints || [];
    return endpoints.find((endpoint) => endpoint.id === selectedEndpointId) || endpoints[0] || null;
  }, [selectedEndpointId, selectedGroup]);
  const selectedBatch = useMemo(() => batches.find((item) => item.id === selectedBatchId) || null, [batches, selectedBatchId]);

  const chooseEndpoint = (groupKey, endpointId, overrides) => {
    const group = groupMap.get(groupKey);
    const endpoint = (group?.endpoints || []).find((item) => item.id === endpointId) || (group?.endpoints || [])[0] || null;
    if (!group || !endpoint) return;
    setSelectedGroupKey(group.key);
    setSelectedEndpointId(endpoint.id);
    setSelectedCaseId(null);
    setCaseName('');
    const next = { ...createInitialRequest(endpoint), ...(overrides || {}) };
    setRequestState(next);
  };

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const data = await getApiCatalog();
      const nextGroups = data?.groups || [];
      setGroups(nextGroups);
      if (nextGroups.length) {
        const firstGroup = nextGroups[0];
        const activeGroup = nextGroups.find((group) => group.key === selectedGroupKey) || firstGroup;
        const activeEndpoint = (activeGroup.endpoints || []).find((item) => item.id === selectedEndpointId) || activeGroup.endpoints?.[0];
        if (activeGroup && activeEndpoint) {
          setSelectedGroupKey(activeGroup.key);
          setSelectedEndpointId(activeEndpoint.id);
          setRequestState((current) => current.endpointId ? current : createInitialRequest(activeEndpoint));
        }
      }
    } catch (error) {
      message.error(error.message || '加载 API 目录失败');
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadCases = async (groupKey) => {
    setCasesLoading(true);
    try {
      const data = await listApiCases(groupKey);
      setCases(data?.records || []);
    } catch (error) {
      message.error(error.message || '加载测试用例失败');
    } finally {
      setCasesLoading(false);
    }
  };

  const loadBatches = async () => {
    setBatchesLoading(true);
    try {
      const data = await listApiBatches();
      const next = data?.records || [];
      setBatches(next);
      if (!selectedBatchId && next.length) setSelectedBatchId(next[0].id);
    } catch (error) {
      message.error(error.message || '加载批量场景失败');
    } finally {
      setBatchesLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
    loadCases();
    loadBatches();
  }, []);

  useEffect(() => {
    if (selectedGroupKey) loadCases(selectedGroupKey);
  }, [selectedGroupKey]);

  const pathParamKeys = extractPathParamKeys(selectedEndpoint?.path);
  const multipartFieldDefs = selectedEndpoint?.multipartFields || [];

  const updateRequest = (patch) => setRequestState((current) => ({ ...current, ...patch }));

  const parseJsonBody = () => {
    if (requestState.bodyType !== 'json') return {};
    try {
      return JSON.parse(requestState.jsonBodyText || '{}');
    } catch (error) {
      throw new Error(`JSON 请求体格式错误: ${error.message}`);
    }
  };

  const buildPayload = () => {
    if (!selectedEndpoint) throw new Error('请先选择接口');

    const payload = {
      endpointId: selectedEndpoint.id,
      method: selectedEndpoint.method,
      path: selectedEndpoint.path,
      bodyType: requestState.bodyType,
      includeAuthToken: requestState.includeAuthToken,
      timeout: requestState.timeout,
      pathParams: requestState.pathParams,
      query: objectFromRows(requestState.queryRows),
      headers: objectFromRows(requestState.headerRows)
    };

    if (requestState.bodyType === 'json') {
      payload.body = parseJsonBody();
    } else if (requestState.bodyType === 'raw') {
      payload.body = { rawBody: requestState.rawBody, contentType: requestState.contentType };
    } else if (requestState.bodyType === 'multipart') {
      payload.body = { multipartFields: objectFromRows(requestState.multipartTextRows) };
    }

    return payload;
  };

  const handleExecute = async () => {
    try {
      const payload = buildPayload();
      setExecuting(true);
      const result = await executeApiRequest(payload, requestState.multipartFiles);
      setResponseState(result?.result || null);
      message.success('接口执行完成');
    } catch (error) {
      message.error(error.message || '接口执行失败');
    } finally {
      setExecuting(false);
    }
  };

  const handleResetRequest = () => {
    if (!selectedEndpoint || !selectedGroup) return;
    chooseEndpoint(selectedGroup.key, selectedEndpoint.id);
    setResponseState(null);
  };

  const handleSaveCase = async (isUpdate) => {
    try {
      const trimmedName = caseName.trim();
      if (!trimmedName) {
        message.warning('请先输入测试用例名称');
        return;
      }
      const payload = { ...buildPayload(), name: trimmedName };
      setCaseSaving(true);
      if (isUpdate && selectedCaseId) {
        await updateApiCase(selectedCaseId, payload);
        message.success('测试用例已更新');
      } else {
        const data = await createApiCase(payload);
        setSelectedCaseId(data?.id || null);
        message.success('测试用例已保存');
      }
      await loadCases(selectedGroupKey);
      await loadBatches();
    } catch (error) {
      message.error(error.message || '保存测试用例失败');
    } finally {
      setCaseSaving(false);
    }
  };

  const loadCase = (record) => {
    const group = groupMap.get(record.group_key);
    const endpoint = (group?.endpoints || []).find((item) => item.id === record.endpoint_id);
    if (!group || !endpoint) {
      message.warning('该用例对应的接口已不在当前目录中');
      return;
    }
    setSelectedGroupKey(group.key);
    setSelectedEndpointId(endpoint.id);
    setSelectedCaseId(record.id);
    setCaseName(record.name || endpoint.name);

    const savedBody = record.body_json || {};
    setRequestState({
      endpointId: endpoint.id,
      method: endpoint.method,
      path: endpoint.path,
      bodyType: record.body_type || endpoint.bodyType || 'none',
      includeAuthToken: Number(record.include_auth_token || 0) === 1,
      timeout: endpoint.bodyType === 'multipart' ? 120000 : 60000,
      pathParams: record.path_params_json || {},
      queryRows: rowsFromObject(record.query_json || {}),
      headerRows: rowsFromObject(record.headers_json || {}),
      jsonBodyText: safeJsonStringify(record.body_type === 'json' ? savedBody : (endpoint.sampleBody || {})),
      rawBody: savedBody?.rawBody || '',
      contentType: savedBody?.contentType || 'text/plain; charset=utf-8',
      multipartTextRows: rowsFromObject(savedBody?.multipartFields || {}),
      multipartFiles: {}
    });
    setResponseState(null);
  };

  const handleDeleteCase = async (record) => {
    try {
      await deleteApiCase(record.id);
      if (record.id === selectedCaseId) {
        setSelectedCaseId(null);
        setCaseName('');
      }
      await loadCases(selectedGroupKey);
      await loadBatches();
      message.success('测试用例已删除');
    } catch (error) {
      message.error(error.message || '删除测试用例失败');
    }
  };

  const openBatchModal = (mode) => {
    const isEdit = mode === 'edit' && selectedBatch;
    setBatchModalMode(mode);
    batchForm.setFieldsValue({
      name: isEdit ? selectedBatch.name : '',
      description: isEdit ? selectedBatch.description : ''
    });
    setBatchModalOpen(true);
  };

  const handleSubmitBatch = async () => {
    try {
      const values = await batchForm.validateFields();
      if (batchModalMode === 'edit' && selectedBatch) {
        await updateApiBatch(selectedBatch.id, values);
        message.success('批量场景已更新');
      } else {
        const data = await createApiBatch(values);
        setSelectedBatchId(data?.id || null);
        message.success('批量场景已创建');
      }
      setBatchModalOpen(false);
      await loadBatches();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || '保存批量场景失败');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    try {
      await deleteApiBatch(batchId);
      if (batchId === selectedBatchId) setSelectedBatchId(null);
      await loadBatches();
      message.success('批量场景已删除');
    } catch (error) {
      message.error(error.message || '删除批量场景失败');
    }
  };

  const handleAddCaseToBatch = async (caseId) => {
    if (!selectedBatch) {
      message.warning('请先选择一个批量场景');
      return;
    }
    try {
      await addApiBatchItem(selectedBatch.id, caseId);
      await loadBatches();
      message.success('已加入批量场景');
    } catch (error) {
      message.error(error.message || '加入批量场景失败');
    }
  };

  const handleReorderBatchItem = async (item, direction) => {
    if (!selectedBatch?.items?.length) return;
    const items = [...selectedBatch.items].sort((a, b) => a.sort_order - b.sort_order);
    const index = items.findIndex((entry) => entry.id === item.id);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || targetIndex < 0 || targetIndex >= items.length) return;
    const current = items[index];
    const target = items[targetIndex];
    try {
      await Promise.all([
        updateApiBatchItem(current.id, target.sort_order),
        updateApiBatchItem(target.id, current.sort_order)
      ]);
      await loadBatches();
    } catch (error) {
      message.error(error.message || '调整顺序失败');
    }
  };

  const handleExecuteBatch = async () => {
    if (!selectedBatch) {
      message.warning('请先选择批量场景');
      return;
    }
    try {
      setBatchExecuting(true);
      const data = await executeApiBatch(selectedBatch.id);
      setBatchResult(data);
      message.success('批量场景执行完成');
    } catch (error) {
      message.error(error.message || '执行批量场景失败');
    } finally {
      setBatchExecuting(false);
    }
  };

  const endpointColumns = [
    {
      title: '方法',
      dataIndex: 'method',
      width: 90,
      render: (value) => <Tag color={METHOD_COLORS[value] || 'default'}>{value}</Tag>
    },
    { title: '名称', dataIndex: 'name', width: 180 },
    { title: '路径', dataIndex: 'path', ellipsis: true },
    { title: '说明', dataIndex: 'description', ellipsis: true }
  ];

  const caseColumns = [
    { title: '名称', dataIndex: 'name' },
    { title: '接口', dataIndex: 'endpoint_id', width: 220, ellipsis: true },
    { title: '更新时间', dataIndex: 'updated_at', width: 180 },
    {
      title: '操作',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => loadCase(record)}>载入</Button>
          <Button size="small" onClick={() => handleAddCaseToBatch(record.id)}>加入批量</Button>
          <Button size="small" danger onClick={() => handleDeleteCase(record)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="API 中心只展示当前账号有分组查看权限的接口；真正发起测试时，还要求具备对应分组的测试权限。"
        />

        <Row gutter={16}>
          <Col xs={24} md={7} lg={6}>
            <Card title="接口分组" loading={catalogLoading} extra={<Button type="link" onClick={loadCatalog}>刷新</Button>}>
              <List
                dataSource={groups}
                locale={{ emptyText: '暂无可见 API 分组' }}
                renderItem={(group) => (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      borderRadius: 8,
                      padding: '12px 14px',
                      background: group.key === selectedGroupKey ? '#f0f5ff' : 'transparent'
                    }}
                    onClick={() => chooseEndpoint(group.key, group.endpoints?.[0]?.id)}
                  >
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space>
                        <Text strong>{group.title}</Text>
                        <Tag color={group.permissions?.canTest ? 'green' : 'default'}>{group.permissions?.canTest ? '可测试' : '仅查看'}</Tag>
                      </Space>
                      <Text type="secondary">{group.description}</Text>
                      <Text type="secondary">接口数：{group.endpoints?.length || 0}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          <Col xs={24} md={17} lg={18}>
            <Card
              title={selectedGroup?.title || '接口列表'}
              extra={<Button icon={<ReloadOutlined />} onClick={loadCatalog}>刷新目录</Button>}
            >
              <Table
                rowKey="id"
                size="small"
                loading={catalogLoading}
                columns={endpointColumns}
                dataSource={selectedGroup?.endpoints || []}
                pagination={false}
                rowSelection={{
                  type: 'radio',
                  selectedRowKeys: selectedEndpoint ? [selectedEndpoint.id] : [],
                  onChange: (keys) => chooseEndpoint(selectedGroupKey, keys[0])
                }}
              />
            </Card>
          </Col>
        </Row>

        {selectedEndpoint ? (
          <>
            <Card
              title={`调试接口：${selectedEndpoint.name}`}
              extra={(
                <Space>
                  <Button onClick={handleResetRequest}>重置</Button>
                  <Button type="primary" loading={executing} onClick={handleExecute} disabled={!selectedGroup?.permissions?.canTest}>立即测试</Button>
                </Space>
              )}
            >
              {!selectedGroup?.permissions?.canTest && (
                <Alert
                  style={{ marginBottom: 16 }}
                  type="warning"
                  showIcon
                  message="当前账号只有查看权限，不能执行测试。"
                />
              )}

              <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="方法"><Tag color={METHOD_COLORS[selectedEndpoint.method] || 'default'}>{selectedEndpoint.method}</Tag></Descriptions.Item>
                <Descriptions.Item label="路径">{selectedEndpoint.path}</Descriptions.Item>
                <Descriptions.Item label="说明" span={2}>{selectedEndpoint.description || '未填写说明'}</Descriptions.Item>
              </Descriptions>

              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <Card size="small" title="基础设置">
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text>携带当前登录令牌</Text>
                        <Switch checked={requestState.includeAuthToken} onChange={(checked) => updateRequest({ includeAuthToken: checked })} />
                      </Space>
                      <div>
                        <Text type="secondary">超时时间（毫秒）</Text>
                        <InputNumber min={1000} step={1000} style={{ width: '100%', marginTop: 6 }} value={requestState.timeout} onChange={(value) => updateRequest({ timeout: value || 60000 })} />
                      </div>
                      {pathParamKeys.length ? (
                        <Card size="small" title="路径参数">
                          <Space direction="vertical" style={{ width: '100%' }}>
                            {pathParamKeys.map((key) => (
                              <Input
                                key={key}
                                addonBefore={key}
                                value={requestState.pathParams[key] || ''}
                                onChange={(event) => updateRequest({ pathParams: { ...requestState.pathParams, [key]: event.target.value } })}
                              />
                            ))}
                          </Space>
                        </Card>
                      ) : null}
                    </Space>
                  </Card>
                </Col>

                <Col xs={24} lg={12}>
                  <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    <KeyValueEditor title="Query 参数" rows={requestState.queryRows} onChange={(rows) => updateRequest({ queryRows: rows })} />
                    <KeyValueEditor title="请求头" rows={requestState.headerRows} onChange={(rows) => updateRequest({ headerRows: rows })} />
                  </Space>
                </Col>
              </Row>

              {requestState.bodyType === 'json' && (
                <Card size="small" title="JSON 请求体" style={{ marginTop: 16 }}>
                  <Input.TextArea
                    rows={12}
                    value={requestState.jsonBodyText}
                    onChange={(event) => updateRequest({ jsonBodyText: event.target.value })}
                  />
                </Card>
              )}

              {requestState.bodyType === 'raw' && (
                <Card size="small" title="Raw 请求体" style={{ marginTop: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      addonBefore="Content-Type"
                      value={requestState.contentType}
                      onChange={(event) => updateRequest({ contentType: event.target.value })}
                    />
                    <Input.TextArea rows={10} value={requestState.rawBody} onChange={(event) => updateRequest({ rawBody: event.target.value })} />
                  </Space>
                </Card>
              )}

              {requestState.bodyType === 'multipart' && (
                <Card size="small" title="Multipart 表单" style={{ marginTop: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    {multipartFieldDefs.filter((field) => field.type === 'file').length ? (
                      <Row gutter={16}>
                        {multipartFieldDefs.filter((field) => field.type === 'file').map((field) => (
                          <Col xs={24} md={12} key={field.key}>
                            <Card size="small" title={`${field.key}${field.required ? ' *' : ''}`}>
                              <Upload
                                multiple
                                beforeUpload={() => false}
                                fileList={requestState.multipartFiles[field.key] || []}
                                onChange={({ fileList }) => updateRequest({ multipartFiles: { ...requestState.multipartFiles, [field.key]: fileList } })}
                              >
                                <Button icon={<UploadOutlined />}>选择文件</Button>
                              </Upload>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : null}
                    <KeyValueEditor title="文本字段" rows={requestState.multipartTextRows} onChange={(rows) => updateRequest({ multipartTextRows: rows })} />
                  </Space>
                </Card>
              )}
            </Card>

            <Card title="执行结果">
              {responseState ? (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Descriptions size="small" bordered column={3}>
                    <Descriptions.Item label="状态码">{responseState.status}</Descriptions.Item>
                    <Descriptions.Item label="耗时">{responseState.durationMs} ms</Descriptions.Item>
                    <Descriptions.Item label="URL">{responseState.url}</Descriptions.Item>
                  </Descriptions>
                  <Card size="small" title="响应头">
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{safeJsonStringify(responseState.headers || {})}</pre>
                  </Card>
                  <Card size="small" title="响应体">
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{typeof responseState.data === 'string' ? responseState.data : safeJsonStringify(responseState.data)}</pre>
                  </Card>
                </Space>
              ) : <Empty description="执行接口后在这里查看响应" />}
            </Card>
          </>
        ) : <Empty description="当前没有可操作的接口" />}

        <Tabs
          items={[
            {
              key: 'cases',
              label: '测试用例',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <Card title="保存当前调试为测试用例">
                    <Space wrap>
                      <Input placeholder="测试用例名称" style={{ width: 320 }} value={caseName} onChange={(event) => setCaseName(event.target.value)} />
                      <Button type="primary" loading={caseSaving} onClick={() => handleSaveCase(false)}>保存为新用例</Button>
                      <Button loading={caseSaving} disabled={!selectedCaseId} onClick={() => handleSaveCase(true)}>更新当前用例</Button>
                    </Space>
                  </Card>
                  <Card title="已保存的测试用例">
                    <Table rowKey="id" loading={casesLoading} columns={caseColumns} dataSource={cases} pagination={{ pageSize: 8 }} />
                  </Card>
                </Space>
              )
            },
            {
              key: 'batches',
              label: '批量执行',
              children: (
                <Row gutter={16}>
                  <Col xs={24} lg={9}>
                    <Card
                      title="批量场景"
                      extra={<Space><Button type="link" onClick={() => openBatchModal('create')}>新建</Button><Button type="link" onClick={loadBatches}>刷新</Button></Space>}
                    >
                      <List
                        loading={batchesLoading}
                        dataSource={batches}
                        locale={{ emptyText: '暂无批量场景' }}
                        renderItem={(item) => (
                          <List.Item
                            style={{
                              cursor: 'pointer',
                              borderRadius: 8,
                              padding: '12px 14px',
                              background: item.id === selectedBatchId ? '#f6ffed' : 'transparent'
                            }}
                            actions={[
                              <Button key="edit" type="link" onClick={() => { setSelectedBatchId(item.id); openBatchModal('edit'); }}>编辑</Button>,
                              <Button key="delete" type="link" danger onClick={() => handleDeleteBatch(item.id)}>删除</Button>
                            ]}
                            onClick={() => setSelectedBatchId(item.id)}
                          >
                            <List.Item.Meta title={item.name} description={`${item.items?.length || 0} 个步骤`} />
                          </List.Item>
                        )}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={15}>
                    <Card
                      title={selectedBatch ? `批量步骤：${selectedBatch.name}` : '批量步骤'}
                      extra={<Button type="primary" loading={batchExecuting} disabled={!selectedBatch} onClick={handleExecuteBatch}>执行场景</Button>}
                    >
                      {selectedBatch ? (
                        <Space direction="vertical" style={{ width: '100%' }} size={16}>
                          <Paragraph type="secondary">{selectedBatch.description || '未填写说明'}</Paragraph>
                          <Table
                            rowKey="id"
                            pagination={false}
                            dataSource={[...(selectedBatch.items || [])].sort((a, b) => a.sort_order - b.sort_order)}
                            columns={[
                              { title: '顺序', dataIndex: 'sort_order', width: 80 },
                              { title: '用例名称', dataIndex: 'case_name' },
                              { title: '接口', dataIndex: 'path', ellipsis: true },
                              {
                                title: '操作',
                                width: 170,
                                render: (_, item) => (
                                  <Space>
                                    <Button size="small" onClick={() => handleReorderBatchItem(item, 'up')}>上移</Button>
                                    <Button size="small" onClick={() => handleReorderBatchItem(item, 'down')}>下移</Button>
                                    <Button size="small" danger onClick={() => deleteApiBatchItem(item.id).then(loadBatches).catch((error) => message.error(error.message || '删除失败'))}>删除</Button>
                                  </Space>
                                )
                              }
                            ]}
                          />
                          <Card size="small" title="最近一次执行结果">
                            {batchResult?.results?.length ? (
                              <List
                                dataSource={batchResult.results}
                                renderItem={(item) => (
                                  <List.Item>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                      <Space>
                                        <Tag color={item.success ? 'green' : 'red'}>{item.success ? '成功' : '失败'}</Tag>
                                        <Text strong>{item.caseName}</Text>
                                        <Text type="secondary">步骤 {item.sortOrder}</Text>
                                      </Space>
                                      <Text type="secondary">{item.success ? `状态码：${item.result?.status}，耗时：${item.result?.durationMs} ms` : item.message}</Text>
                                    </Space>
                                  </List.Item>
                                )}
                              />
                            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="执行批量场景后在这里显示结果" />}
                          </Card>
                        </Space>
                      ) : <Empty description="请选择左侧批量场景" />}
                    </Card>
                  </Col>
                </Row>
              )
            }
          ]}
        />
      </Space>

      <Modal
        open={batchModalOpen}
        title={batchModalMode === 'edit' ? '编辑批量场景' : '新建批量场景'}
        onCancel={() => setBatchModalOpen(false)}
        onOk={handleSubmitBatch}
        destroyOnClose
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item label="场景名称" name="name" rules={[{ required: true, message: '请输入场景名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="说明" name="description">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}